import React, { useState, useEffect, useRef } from 'react';
import { useAuth, API_URL, Currency, DateBR } from '../contexts/AuthContext';
import { DollarSign, CreditCard, Search } from 'lucide-react';

export default function Fiado() {
  const { token } = useAuth();
  const [debtors, setDebtors] = useState<any[]>([]);
  const [receivables, setReceivables] = useState<any[]>([]);
  const [openPay, setOpenPay] = useState(false);
  const [recId, setRecId] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [session, setSession] = useState<any>(null);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/customers/debtors`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(d => setDebtors(d)).catch(() => {});
    fetch(`${API_URL}/customers/receivables?status=open`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(d => setReceivables(d)).catch(() => {});
    fetch(`${API_URL}/cash/session/current`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null).then(s => setSession(s)).catch(() => {});
  }, [token]);

  const pay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recId || !payAmount) return;
    try {
      const r = await fetch(`${API_URL}/customers/receivables/${recId}/pay`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ amount: parseFloat(payAmount), payment_method: 'money', cash_session_id: session?.id }) });
      const j = await r.json();
      if (!r.ok) { setMsg(j.error || 'Erro'); return; }
      setMsg(`Recebido R$ ${j.newPaid}. Saldo restante: R$ ${j.debt}`), setOpenPay(false);
      fetch(`${API_URL}/customers/debtors`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(d => setDebtors(d)).catch(() => {});
      fetch(`${API_URL}/customers/receivables?status=open`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(d => setReceivables(d)).catch(() => {});
    } catch { setMsg('Erro interno'); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Fiado / Clientes Devedores</h1>
        <p className="text-gray-600">Controle de vendas a crédito e recebimentos</p>
      </div>
      {msg && <div className="rounded-lg p-3 text-sm bg-green-50 border border-green-200 text-green-700">{msg}</div>}
      {!session || session.status !== 'open' && <div className="rounded-lg p-3 text-sm bg-amber-50 border border-amber-200 text-amber-700">Abra o caixa em Caixa antes de registrar recebimentos.</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-sm font-semibold text-gray-700 mb-3"><DollarSign size={16} className="mr-2" /> Devedores</h2>
          <div className="space-y-2">
            {debtors.map(d => (
              <div key={d.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div>
                  <div className="font-medium text-sm">{d.name}</div>
                  <div className="text-xs text-gray-500">{d.phone || '-'} · última compra {d.last_charge ? DateBR({ iso: d.last_charge }) : '-'}</div>
                </div>
                <div className="text-right"><div className="font-bold text-red-600">{Currency(d.debt)}</div><div className="text-xs text-gray-500">pago {Currency(d.total_paid)}</div></div>
              </div>
            ))}
            {debtors.length === 0 && <p className="text-xs text-gray-500 text-center py-4">Sem devedores</p>}
          </div>
        </div>
        <div className="card">
          <h2 className="text-sm font-semibold text-gray-700 mb-3"><CreditCard size={16} className="mr-2" /> Contas a receber (em aberto)</h2>
          <div className="space-y-2">
            {receivables.map(r => (
              <div key={r.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 text-sm">
                <div>
                  <div className="font-medium">{r.customer_name}</div>
                  <div className="text-xs text-gray-500">{r.description} · venc. {r.due_date ? DateBR({ iso: r.due_date }) : '-'}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right font-bold">{Currency(r.total_amount - r.paid_amount)}</div>
                  <button onClick={() => { setRecId(r.id); setOpenPay(true); }} className="text-xs btn-primary">Receber</button>
                </div>
              </div>
            ))}
            {receivables.length === 0 && <p className="text-xs text-gray-500 text-center py-4">Nenhuma conta em aberto</p>}
          </div>
        </div>
      </div>
      {openPay && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Receber pagamento</h2>
            <form onSubmit={pay} className="space-y-3">
              <div><label className="text-xs font-medium text-gray-500 block mb-1">Valor a receber (R$)</label><input className="input" type="number" step="0.01" value={payAmount} onChange={e => setPayAmount(e.target.value)} required /></div>
              <div className="flex justify-end gap-2 pt-4">
                <button type="button" onClick={() => setOpenPay(false)} className="btn-ghost px-4">Cancelar</button>
                <button className="btn-success">Confirmar recebimento</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
